/**
 * Amenity detection — find the lifts, escalators and restrooms drawn on a plan.
 *
 * What is honestly detectable from a raster, and what is not:
 *
 *   DETECTABLE. Mall plans mark amenities with a BOXED PICTOGRAM — a small
 *   near-square outline with a glyph inside, sitting in the circulation. That
 *   is a strong, checkable signature: closed border, icon-sized, moderate ink.
 *   Escalators and stairs are drawn as HATCHED BANDS, which is an equally
 *   strong signature: a regular alternation of ink and paper along one axis.
 *
 *   NOT DETECTABLE. Which pictogram it is. Telling a wheelchair from a baby
 *   changing table from a prayer mat needs a trained classifier and training
 *   data neither of which exists here. Guessing would quietly put a lift on a
 *   floor that has none and route people through it.
 *
 * So this module PROPOSES markers with a type only where the geometry proves it
 * (hatching -> vertical circulation), leaves the rest untyped, and the importer
 * makes typing them a one-click job. Anything missed can be dropped on the plan
 * by hand. Nothing is auto-published untyped.
 */

/** Types an imported amenity can carry. Order drives the picker. */
export const AMENITY_TYPES = [
    'Escalator', 'Lift', 'Stairs', 'Restrooms', 'Prayer Room', 'Baby Care',
    'Information', 'Entrance', 'Parking', 'ATM', 'First Aid', 'Smoking Area',
];

/** Only these actually connect floors, so only these get vertical edges. */
export const VERTICAL_TYPES = new Set(['Escalator', 'Lift', 'Stairs']);

export const AMENITY_DEFAULTS = {
    /** Icon side length as a fraction of the sheet diagonal — min and max. */
    minSizeFraction: 0.006,
    maxSizeFraction: 0.055,
    /** How square a pictogram box has to be. */
    maxAspect: 2.2,
    /** Fraction of the bbox border that must be inked for a "boxed" glyph. */
    minPerimeterInk: 0.58,
    /** Ink coverage inside the bbox — text is sparse, a solid blob is not a glyph. */
    minFill: 0.16,
    maxFill: 0.9,
    /** Alternations of ink/paper needed to call something hatching. */
    minStripes: 5,
    /** Ink components are clustered when their gap is under this × icon size. */
    clusterGapFraction: 0.35,
    /**
     * A marker has to sit on neutral paper, not inside a coloured shop fill.
     *
     * Without this, printed unit numbers pass every other test: a row of digits
     * is icon-sized, near-square once clustered, and regularly striped — so
     * "137" over a shop gets proposed as an escalator. But amenities are always
     * drawn in the circulation, so the wash a candidate sits on settles it.
     */
    requireNeutralContext: true,
    /** Saturation above which the surrounding wash counts as a shop fill. */
    contextChroma: 0.09,
};

/**
 * Connected components of INK (dark pixels), 8-connected.
 * Returns components with a bbox and a pixel mask offset into that bbox.
 */
function inkComponents(imageData, wallLuminance, maxSidePx) {
    const { data, width, height } = imageData;
    const ink = new Uint8Array(width * height);
    for (let i = 0, p = 0; i < ink.length; i++, p += 4) {
        if (data[p + 3] < 32) continue;
        const lum = (0.2126 * data[p]) + (0.7152 * data[p + 1]) + (0.0722 * data[p + 2]);
        if (lum < wallLuminance) ink[i] = 1;
    }

    const seen = new Uint8Array(width * height);
    const stack = new Int32Array(width * height);
    const out = [];

    for (let seed = 0; seed < ink.length; seed++) {
        if (!ink[seed] || seen[seed]) continue;
        let sp = 0;
        stack[sp++] = seed;
        seen[seed] = 1;

        const pixels = [];
        let minX = width; let minY = height; let maxX = -1; let maxY = -1;
        let overflowed = false;

        while (sp > 0) {
            const idx = stack[--sp];
            const x = idx % width;
            const y = (idx - x) / width;
            pixels.push(idx);
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;

            // The wall network is one enormous component. Abandon it early
            // rather than collecting a million pixels we will only discard.
            if (!overflowed && (maxX - minX > maxSidePx || maxY - minY > maxSidePx)) overflowed = true;
            if (overflowed && pixels.length > 40000) break;

            for (let dy = -1; dy <= 1; dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                    if (!dx && !dy) continue;
                    const nx = x + dx;
                    const ny = y + dy;
                    if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
                    const n = (ny * width) + nx;
                    if (ink[n] && !seen[n]) { seen[n] = 1; stack[sp++] = n; }
                }
            }
        }
        // Drain anything left so the component is fully marked as seen.
        while (sp > 0) { seen[stack[--sp]] = 1; }

        if (overflowed) continue;
        out.push({ minX, minY, maxX, maxY, pixels });
    }

    return { components: out, ink, width, height };
}

/** Merge components whose bboxes sit within `gap` of each other. */
function cluster(components, gap) {
    const used = new Uint8Array(components.length);
    const groups = [];

    const near = (a, b) => (
        a.minX - gap <= b.maxX && b.minX - gap <= a.maxX
        && a.minY - gap <= b.maxY && b.minY - gap <= a.maxY
    );

    for (let i = 0; i < components.length; i++) {
        if (used[i]) continue;
        used[i] = 1;
        const group = { minX: components[i].minX, minY: components[i].minY, maxX: components[i].maxX, maxY: components[i].maxY, members: [i] };
        let grew = true;
        while (grew) {
            grew = false;
            for (let j = 0; j < components.length; j++) {
                if (used[j]) continue;
                if (!near(group, components[j])) continue;
                used[j] = 1;
                group.members.push(j);
                group.minX = Math.min(group.minX, components[j].minX);
                group.minY = Math.min(group.minY, components[j].minY);
                group.maxX = Math.max(group.maxX, components[j].maxX);
                group.maxY = Math.max(group.maxY, components[j].maxY);
                grew = true;
            }
        }
        groups.push(group);
    }
    return groups;
}

/** Fraction of the bbox border pixels that are inked. */
function perimeterInk(ink, width, box) {
    let on = 0;
    let total = 0;
    const at = (x, y) => ink[(y * width) + x];
    for (let x = box.minX; x <= box.maxX; x++) {
        total += 2;
        if (at(x, box.minY)) on += 1;
        if (at(x, box.maxY)) on += 1;
    }
    for (let y = box.minY + 1; y < box.maxY; y++) {
        total += 2;
        if (at(box.minX, y)) on += 1;
        if (at(box.maxX, y)) on += 1;
    }
    return total ? on / total : 0;
}

/**
 * Count ink/paper alternations across the box on its short axis.
 *
 * Escalator and stair runs are drawn as evenly spaced treads. A run of regular
 * alternations is the one amenity signature the drawing states outright, so it
 * is the one type this module is willing to assign by itself.
 */
function stripeScore(ink, width, box) {
    const w = box.maxX - box.minX + 1;
    const h = box.maxY - box.minY + 1;
    const alongX = w >= h;              // treads run across the short axis
    const n = alongX ? w : h;
    const m = alongX ? h : w;
    if (n < 8 || m < 3) return { stripes: 0, regular: false };

    const profile = new Float32Array(n);
    for (let i = 0; i < n; i++) {
        let on = 0;
        for (let j = 0; j < m; j++) {
            const x = alongX ? box.minX + i : box.minX + j;
            const y = alongX ? box.minY + j : box.minY + i;
            if (ink[(y * width) + x]) on += 1;
        }
        profile[i] = on / m;
    }

    // Alternations above/below the midpoint of the profile's own range.
    let lo = Infinity; let hi = -Infinity;
    for (let i = 0; i < n; i++) { if (profile[i] < lo) lo = profile[i]; if (profile[i] > hi) hi = profile[i]; }
    if (hi - lo < 0.35) return { stripes: 0, regular: false };
    const mid = (lo + hi) / 2;

    const edges = [];
    let state = profile[0] > mid;
    for (let i = 1; i < n; i++) {
        const s = profile[i] > mid;
        if (s !== state) { edges.push(i); state = s; }
    }
    if (edges.length < 4) return { stripes: edges.length, regular: false };

    const gaps = [];
    for (let i = 1; i < edges.length; i++) gaps.push(edges[i] - edges[i - 1]);
    const mean = gaps.reduce((s, g) => s + g, 0) / gaps.length;
    if (mean < 1.2) return { stripes: edges.length, regular: false };
    const dev = Math.sqrt(gaps.reduce((s, g) => s + ((g - mean) ** 2), 0) / gaps.length);

    return { stripes: edges.length, regular: dev / mean < 0.45 };
}

/**
 * Mean chroma of the non-ink pixels in and just around a candidate's box.
 * Near zero on paper; high inside a category wash.
 */
function contextChroma(data, width, height, box, wallLuminance) {
    const pad = Math.max(3, Math.round(Math.max(box.maxX - box.minX, box.maxY - box.minY) * 0.4));
    const x0 = Math.max(0, box.minX - pad);
    const x1 = Math.min(width - 1, box.maxX + pad);
    const y0 = Math.max(0, box.minY - pad);
    const y1 = Math.min(height - 1, box.maxY + pad);

    let sum = 0;
    let n = 0;
    for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
            const p = ((y * width) + x) * 4;
            if (data[p + 3] < 32) continue;
            const r = data[p];
            const g = data[p + 1];
            const b = data[p + 2];
            if ((0.2126 * r) + (0.7152 * g) + (0.0722 * b) < wallLuminance) continue;   // ink
            sum += (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
            n += 1;
        }
    }
    return n ? sum / n : 0;
}

/**
 * Propose amenity markers on a plan.
 *
 * @param {ImageData} imageData working-resolution plan (same raster segmentPlan used)
 * @param {object} options { wallLuminance, ...AMENITY_DEFAULTS }
 * @returns {Array} markers, positions NORMALISED to 0..1
 */
export function detectAmenities(imageData, options = {}) {
    const opts = { ...AMENITY_DEFAULTS, ...options };
    const wallLuminance = options.wallLuminance != null ? options.wallLuminance : 105;
    const { width, height, data } = imageData;
    const diagonal = Math.hypot(width, height);

    const minSide = opts.minSizeFraction * diagonal;
    const maxSide = opts.maxSizeFraction * diagonal;

    const { components, ink } = inkComponents(imageData, wallLuminance, maxSide * 1.6);
    if (!components.length) return [];

    const groups = cluster(components, Math.max(2, maxSide * opts.clusterGapFraction));
    const markers = [];

    groups.forEach((box, i) => {
        const w = box.maxX - box.minX + 1;
        const h = box.maxY - box.minY + 1;
        const long = Math.max(w, h);
        const short = Math.min(w, h);
        if (long < minSide || long > maxSide * 1.9) return;
        if (short < minSide * 0.45) return;

        if (opts.requireNeutralContext
            && contextChroma(data, width, height, box, wallLuminance) > opts.contextChroma) return;

        let inkPx = 0;
        box.members.forEach((m) => { inkPx += components[m].pixels.length; });
        const fill = inkPx / (w * h);

        const stripes = stripeScore(ink, width, box);
        const isHatched = stripes.regular && stripes.stripes >= opts.minStripes;

        if (!isHatched) {
            // A boxed pictogram: near-square, closed border, moderate ink.
            if (long / short > opts.maxAspect) return;
            if (fill < opts.minFill || fill > opts.maxFill) return;
            if (perimeterInk(ink, width, box) < opts.minPerimeterInk) return;
        }

        markers.push({
            id: `am${i}`,
            x: ((box.minX + box.maxX) / 2) / width,
            y: ((box.minY + box.maxY) / 2) / height,
            wN: w / width,
            hN: h / height,
            // Hatching proves vertical circulation; a pictogram proves nothing
            // beyond "there is an amenity here". Untyped markers are not
            // published until the reviewer says what they are.
            type: isHatched ? 'Escalator' : null,
            confidence: isHatched ? 'hatching' : 'pictogram',
            name: '',
            auto: true,
        });
    });

    // Strongest signal first, then biggest.
    markers.sort((a, b) => {
        if ((a.confidence === 'hatching') !== (b.confidence === 'hatching')) return a.confidence === 'hatching' ? -1 : 1;
        return (b.wN * b.hN) - (a.wN * a.hN);
    });
    return markers.slice(0, 80);
}

/** A default display name when the reviewer types a marker but doesn't name it. */
export function amenityLabel(marker, index) {
    if (marker.name) return marker.name;
    if (marker.type) return `${marker.type} ${index + 1}`;
    return `Marker ${index + 1}`;
}
